import { useState } from 'react';
import { Layers, Plus, Tag, Tags } from 'lucide-react';
import { P } from '@stockpilot/shared';
import {
  useBrandsQuery, useCategoriesQuery, useCreateUnitConversionMutation,
  useDeleteBrandMutation, useDeleteCategoryMutation, useDeleteUnitConversionMutation,
  useDeleteUnitMutation, useUnitConversionsQuery, useUnitsQuery, useUpdateCategoryMutation,
  type Brand, type Category, type Unit, type UnitConversion,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import {
  Alert, Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { CategoryPicker } from '@/components/CategoryPicker';
import { UnitPicker } from '@/components/UnitPicker';

type Tab = 'categories' | 'brands' | 'units';

const TABS: { id: Tab; label: string }[] = [
  { id: 'categories', label: 'Categories' },
  { id: 'brands', label: 'Brands' },
  { id: 'units', label: 'Units & conversions' },
];

/**
 * Maintenance for the catalogue's master data.
 *
 * Creating any of these is possible inline from the product form; this is where
 * they are renamed, re-parented and archived. Every archive here is refused by
 * the API while anything still references the record, so the failure message is
 * surfaced rather than swallowed.
 */
export function CatalogPage() {
  const [tab, setTab] = useState<Tab>('categories');

  return (
    <>
      <PageHeader
        title="Catalogue"
        description="Categories, brands and units of measure — the master data products are built from."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((entry) => (
          <Button
            key={entry.id}
            size="sm"
            variant={tab === entry.id ? 'primary' : 'outline'}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      {tab === 'categories' && <CategoriesPanel />}
      {tab === 'brands' && <BrandsPanel />}
      {tab === 'units' && <UnitsPanel />}
    </>
  );
}

// ---------------------------------------------------------------- categories

/**
 * Orders a flat category list parents-first, each followed by its children, and
 * records the depth so the table can indent.
 *
 * Deliberately not `GET /categories/tree`: that projection omits the product and
 * child counts, and those counts are exactly what tells you whether a category
 * can be archived.
 */
function asTree(categories: Category[]): { category: Category; depth: number }[] {
  const byParent = new Map<string | null, Category[]>();
  for (const category of categories) {
    const key = category.parentId;
    byParent.set(key, [...(byParent.get(key) ?? []), category]);
  }

  const known = new Set(categories.map((category) => category.id));
  const rows: { category: Category; depth: number }[] = [];

  const walk = (parentId: string | null, depth: number): void => {
    for (const category of byParent.get(parentId) ?? []) {
      rows.push({ category, depth });
      walk(category.id, depth + 1);
    }
  };
  walk(null, 0);

  // A child whose parent fell outside the page would otherwise vanish.
  for (const category of categories) {
    if (category.parentId && !known.has(category.parentId)) {
      rows.push({ category, depth: 0 });
    }
  }

  return rows;
}

function CategoriesPanel() {
  const { data, isLoading, isError, error, refetch } = useCategoriesQuery({ limit: 200 });
  const [updateCategory, updateState] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState({ name: '', parentId: '' });
  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const open = (category: Category): void => {
    setFailure(null);
    setEditing(category);
    setForm({ name: category.name, parentId: category.parentId ?? '' });
  };

  const save = async (): Promise<void> => {
    if (!editing) return;
    setFailure(null);
    try {
      await updateCategory({
        id: editing.id,
        body: { name: form.name, parentId: form.parentId || null },
      }).unwrap();
      setEditing(null);
    } catch (saveError) {
      // Cycles and duplicate sibling names are both refused by the API.
      setFailure(apiErrorMessage(saveError, 'That category could not be saved'));
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setFailure(null);
    try {
      await deleteCategory(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deleteError) {
      setFailure(apiErrorMessage(deleteError, 'That category could not be archived'));
    }
  };

  return (
    <Card>
      <CardHeader
        title="Categories"
        description="Nest categories to group products. A category in use cannot be archived."
      />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyState
          icon={Tags}
          title="No categories yet"
          description="Categories can be created while adding a product."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Category</Th>
              <Th>Inside</Th>
              <Th numeric>Sub-categories</Th>
              <Th numeric>Products</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {asTree(data?.items ?? []).map(({ category, depth }) => (
              <tr key={category.id} className="hover:bg-sunken">
                <Td className="font-medium">
                  <span style={{ paddingLeft: `${depth * 1.25}rem` }}>
                    {depth > 0 && <span className="mr-1 text-muted">└</span>}
                    {category.name}
                  </span>
                </Td>
                <Td className="text-secondary">{category.parent?.name ?? 'Top level'}</Td>
                <Td numeric className="text-secondary">{category._count?.children ?? 0}</Td>
                <Td numeric className="text-secondary">{category._count?.products ?? 0}</Td>
                <Td className="text-right">
                  <Can permission={P.CATEGORY_MANAGE}>
                    <Button size="sm" variant="ghost" onClick={() => open(category)}>Edit</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setFailure(null);
                        setPendingDelete(category);
                      }}
                    >
                      Archive
                    </Button>
                  </Can>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit category"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={updateState.isLoading} disabled={!form.name} onClick={() => void save()}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}
          <Input
            label="Name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <CategoryPicker
            label="Inside"
            value={form.parentId}
            onChange={(parentId) => setForm({ ...form, parentId })}
            // A category cannot be its own parent; the API refuses the rest of
            // the subtree too.
            categories={(data?.items ?? []).filter((row) => row.id !== editing?.id)}
            hint="Leave blank for a top-level category."
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setFailure(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Archive this category?"
        message={
          failure ??
          `${pendingDelete?.name ?? ''} will be archived. Categories with sub-categories or products cannot be archived.`
        }
        confirmLabel="Archive"
      />
    </Card>
  );
}

// -------------------------------------------------------------------- brands

function BrandsPanel() {
  const { data, isLoading, isError, error, refetch } = useBrandsQuery({ limit: 200 });
  const [deleteBrand] = useDeleteBrandMutation();
  const [pendingDelete, setPendingDelete] = useState<Brand | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setFailure(null);
    try {
      await deleteBrand(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deleteError) {
      setFailure(apiErrorMessage(deleteError, 'That brand could not be archived'));
    }
  };

  return (
    <Card>
      <CardHeader title="Brands" description="A brand in use by any product cannot be archived." />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No brands yet"
          description="Brands can be created while adding a product."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Brand</Th>
              <Th>Description</Th>
              <Th numeric>Products</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {data?.items.map((brand) => (
              <tr key={brand.id} className="hover:bg-sunken">
                <Td className="font-medium">{brand.name}</Td>
                <Td className="text-secondary">{brand.description ?? '—'}</Td>
                <Td numeric className="text-secondary">{brand._count?.products ?? 0}</Td>
                <Td className="text-right">
                  <Can permission={P.CATEGORY_MANAGE}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setFailure(null);
                        setPendingDelete(brand);
                      }}
                    >
                      Archive
                    </Button>
                  </Can>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setFailure(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Archive this brand?"
        message={failure ?? `${pendingDelete?.name ?? ''} will be archived.`}
        confirmLabel="Archive"
      />
    </Card>
  );
}

// --------------------------------------------------------------------- units

function UnitsPanel() {
  const units = useUnitsQuery({ limit: 100 });
  const conversions = useUnitConversionsQuery({ limit: 200 });
  const [deleteUnit] = useDeleteUnitMutation();
  const [createConversion, createState] = useCreateUnitConversionMutation();
  const [deleteConversion] = useDeleteUnitConversionMutation();

  const [pendingDelete, setPendingDelete] = useState<Unit | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ fromUnitId: '', toUnitId: '', factor: '' });

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setFailure(null);
    try {
      await deleteUnit(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deleteError) {
      // The API lists exactly what still references the unit.
      setFailure(apiErrorMessage(deleteError, 'That unit could not be archived'));
    }
  };

  const saveConversion = async (): Promise<void> => {
    setFailure(null);
    try {
      await createConversion({
        fromUnitId: form.fromUnitId,
        toUnitId: form.toUnitId,
        factor: Number(form.factor),
      }).unwrap();
      setForm({ fromUnitId: '', toUnitId: '', factor: '' });
      setAdding(false);
    } catch (saveError) {
      setFailure(apiErrorMessage(saveError, 'That conversion could not be saved'));
    }
  };

  const drop = async (id: string): Promise<void> => {
    setFailure(null);
    try {
      await deleteConversion(id).unwrap();
    } catch (deleteError) {
      setFailure(apiErrorMessage(deleteError, 'That conversion could not be removed'));
    }
  };

  const unitList = units.data?.items ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Units of measure"
          description="A unit referenced by any product, conversion or ledger entry cannot be archived."
        />

        {units.isLoading ? (
          <LoadingState />
        ) : units.isError ? (
          <ErrorState message={apiErrorMessage(units.error)} onRetry={() => void units.refetch()} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Unit</Th>
                <Th>Symbol</Th>
                <Th>Measures</Th>
                <Th numeric>Decimals</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {unitList.map((unit) => (
                <tr key={unit.id} className="hover:bg-sunken">
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{unit.name}</span>
                      {unit.isBaseUnit && <Badge tone="brand">Reference</Badge>}
                    </div>
                  </Td>
                  <Td className="text-secondary">{unit.symbol}</Td>
                  <Td className="text-secondary">{unit.dimension.toLowerCase()}</Td>
                  <Td numeric className="text-secondary">{unit.precision}</Td>
                  <Td className="text-right">
                    <Can permission={P.UNIT_MANAGE}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setFailure(null);
                          setPendingDelete(unit);
                        }}
                      >
                        Archive
                      </Button>
                    </Can>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Conversions"
          description="How one unit converts into another. Only allowed within the same dimension."
          action={
            <Can permission={P.UNIT_MANAGE}>
              <Button size="sm" icon={Plus} onClick={() => setAdding(true)} disabled={adding}>
                New conversion
              </Button>
            </Can>
          }
        />

        <div className="space-y-4 p-4">
          {failure && <Alert tone="danger">{failure}</Alert>}

          {adding && (
            <div className="space-y-3 rounded-lg border border-subtle bg-sunken p-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <UnitPicker
                  label="From"
                  value={form.fromUnitId}
                  onChange={(fromUnitId) => setForm({ ...form, fromUnitId })}
                  units={unitList}
                  excludeIds={form.toUnitId ? [form.toUnitId] : []}
                />
                <UnitPicker
                  label="To"
                  value={form.toUnitId}
                  onChange={(toUnitId) => setForm({ ...form, toUnitId })}
                  units={unitList}
                  excludeIds={form.fromUnitId ? [form.fromUnitId] : []}
                />
                <Input
                  label="Factor"
                  type="number"
                  min="0"
                  step="0.000001"
                  value={form.factor}
                  onChange={(event) => setForm({ ...form, factor: event.target.value })}
                  hint="1 from-unit = this many to-units."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button
                  size="sm"
                  variant="outline"
                  loading={createState.isLoading}
                  disabled={
                    !form.fromUnitId || !form.toUnitId || !form.factor || Number(form.factor) <= 0
                  }
                  onClick={() => void saveConversion()}
                >
                  Save conversion
                </Button>
              </div>
            </div>
          )}

          {conversions.isLoading ? (
            <LoadingState />
          ) : conversions.data && conversions.data.items.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No conversions yet"
              description="Add one so products can be bought or sold in a different unit than they are stocked in."
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Conversion</Th>
                  <Th>Scope</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {conversions.data?.items.map((conversion: UnitConversion) => (
                  <tr key={conversion.id} className="hover:bg-sunken">
                    <Td>
                      1 {conversion.fromUnit.symbol} = {conversion.factor}{' '}
                      {conversion.toUnit.symbol}
                    </Td>
                    <Td className="text-secondary">
                      {conversion.product
                        ? `${conversion.product.name} only`
                        : 'Whole business'}
                    </Td>
                    <Td className="text-right">
                      <Can permission={P.UNIT_MANAGE}>
                        <Button size="sm" variant="ghost" onClick={() => void drop(conversion.id)}>
                          Remove
                        </Button>
                      </Can>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setFailure(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Archive this unit?"
        message={
          failure ??
          `${pendingDelete?.name ?? ''} will be archived. Units already in use cannot be archived.`
        }
        confirmLabel="Archive"
      />
    </div>
  );
}
